import { useCallback, useEffect, useState } from 'react';

import { move } from '@dnd-kit/helpers';
import type { DragEndEvent } from '@dnd-kit/react';
import orderBy from 'lodash.orderby';

import { useDataTable } from '@/_containers';
import { useBoolean } from '@/_shared';

import { useDefineColumns } from './use-define-columns';

import { usePickTicketsFormContext } from '../../../form-context';
import type { PickTicketsItemFormSchema } from '../../../pick-ticket-items-schemas';

export interface TableViewProps {
  tableData: PickTicketsItemFormSchema[];
  isEditable: boolean;
  cacheBuster: number | string;
  handleDelete?: (id: number) => void;
  handleDataUpdate: (v: PickTicketsItemFormSchema[]) => void;
}

export function useTableView({ tableData, cacheBuster, handleDelete, handleDataUpdate, isEditable }: TableViewProps) {
  const [shouldUpdate, setShouldUpdate] = useBoolean(false);
  const [shouldSort, setShouldSort] = useBoolean(false);

  const [data, setData] = useState(orderBy(tableData, ['sort_order']));

  const form = usePickTicketsFormContext();

  const onCellBlur = useCallback(
    (index: number, id: number, name: keyof PickTicketsItemFormSchema, v: number) => {
      form.setValue(`pick_ticket_items.${index}.${name}`, v, {
        shouldDirty: true,
      });
      form.setValue(`pick_ticket_items.${index}.id`, id, {
        shouldDirty: true,
      });
      setData((old) =>
        old.map((row, index) => {
          if (row.id === id) {
            return {
              ...old[index]!,
              [name]: v,
            };
          }
          return row;
        })
      );
      setShouldUpdate.setTrue();
    },
    [form, setShouldUpdate]
  );

  const getRowId = useCallback((row: PickTicketsItemFormSchema) => `${row.sort_order}`, []);

  const columns = useDefineColumns(isEditable, onCellBlur, handleDelete);
  const table = useDataTable({ columns, data, getRowId });

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (event.canceled) return;
      setData((old) => move(old, event));
      setShouldSort.setTrue();
    },
    [setShouldSort]
  );

  // on add or remove row from table
  useEffect(() => {
    if (tableData.length !== data.length) {
      if (tableData.length > data.length) {
        tableData.forEach((row) => {
          if (!data.find((d) => d.id === row.id)) {
            setData((old) => [...old, row]);
          }
        });
      }

      if (tableData.length < data.length) {
        data.forEach((row) => {
          if (!tableData.find((d) => d.id === row.id)) {
            setData((old) => old.filter((d) => d.id !== row.id));
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length, tableData.length]);

  // on edit mode change
  useEffect(() => {
    if (!isEditable) {
      setData(orderBy(tableData, ['sort_order']));
    }
  }, [isEditable, tableData]);

  // should sort before updating global state
  useEffect(() => {
    if (shouldSort) {
      setShouldSort.setFalse();

      setData((old) => {
        // TO SEE THE SORT ORDER
        const sortOrder = orderBy(old.map((r) => r.sort_order));
        return old.map((item, index) => ({ ...item, sort_order: sortOrder[index] }));
      });

      setShouldUpdate.setTrue();
    }
  }, [setShouldSort, setShouldUpdate, shouldSort]);

  // should update global state
  useEffect(() => {
    if (shouldUpdate) {
      setShouldUpdate.setFalse();
      handleDataUpdate(data);
    }
  }, [data, handleDataUpdate, setShouldUpdate, shouldUpdate]);

  return {
    cacheBuster,
    tableData,
    isEditable,
    data,
    table,
    handleDragEnd,
    getRowId,
  };
}
